import type { Command, CommandContext } from '../types/command.types';

/**
 * Service for managing command registration and execution
 * Provides a centralized registry for commands that can be accessed via the command palette
 */
export class CommandRegistryService {
  private commands: Map<string, Command> = new Map();
  private listeners: Set<() => void> = new Set();

  /**
   * Register one or more commands
   * @param commands - Array of commands to register
   */
  registerCommands(commands: Command[]): void {
    commands.forEach((command) => {
      this.commands.set(command.id, command);
    });
    this.notifyListeners();
  }

  /**
   * Register a single command
   * @param command - Command to register
   */
  registerCommand(command: Command): void {
    this.commands.set(command.id, command);
    this.notifyListeners();
  }

  /**
   * Unregister commands by ID
   * @param commandIds - Array of command IDs to unregister
   */
  unregisterCommands(commandIds: string[]): void {
    commandIds.forEach((id) => {
      this.commands.delete(id);
    });
    this.notifyListeners();
  }

  /**
   * Unregister a single command
   * @param commandId - Command ID to unregister
   */
  unregisterCommand(commandId: string): void {
    this.commands.delete(commandId);
    this.notifyListeners();
  }

  /**
   * Get all registered commands
   * @param context - Command context for availability checks
   * @returns Array of available commands
   */
  getCommands(context?: CommandContext): Command[] {
    const allCommands = Array.from(this.commands.values());

    if (!context) {
      return allCommands;
    }

    // Filter by availability
    return allCommands.filter((command) => {
      if (!command.isAvailable) {
        return true; // No availability check means always available
      }
      return command.isAvailable(context);
    });
  }

  /**
   * Get a command by ID
   * @param commandId - Command ID
   * @returns Command if found, undefined otherwise
   */
  getCommand(commandId: string): Command | undefined {
    return this.commands.get(commandId);
  }

  /**
   * Execute a command by ID
   * @param commandId - Command ID to execute
   * @param context - Command context
   */
  async executeCommand(commandId: string, context: CommandContext): Promise<void> {
    const command = this.commands.get(commandId);
    if (!command) {
      console.warn(`Command not found: ${commandId}`);
      return;
    }

    // Check availability
    if (command.isAvailable && !command.isAvailable(context)) {
      console.warn(`Command not available: ${commandId}`);
      return;
    }

    try {
      await command.execute(context);

      // Emit event for analytics/logging
      context.events?.emit({
        type: 'command:executed',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: {
          commandId: command.id,
          commandLabel: command.label,
        },
      });
    } catch (error) {
      console.error(`Error executing command ${commandId}:`, error);

      // Emit error event
      context.events?.emit({
        type: 'command:error',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: {
          commandId: command.id,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  /**
   * Search commands by query
   * @param query - Search query
   * @param context - Command context for availability checks
   * @returns Filtered and sorted commands
   */
  searchCommands(query: string, context?: CommandContext): Command[] {
    const availableCommands = this.getCommands(context);

    if (!query.trim()) {
      return this.sortCommands(availableCommands);
    }

    const lowerQuery = query.toLowerCase();

    // Score each command based on matching
    const scored = availableCommands.map((command) => {
      let score = 0;

      // Exact label match (highest priority)
      if (command.label.toLowerCase() === lowerQuery) {
        score += 1000;
      }
      // Label starts with query
      else if (command.label.toLowerCase().startsWith(lowerQuery)) {
        score += 500;
      }
      // Label contains query
      else if (command.label.toLowerCase().includes(lowerQuery)) {
        score += 250;
      }

      // Description contains query
      if (command.description?.toLowerCase().includes(lowerQuery)) {
        score += 100;
      }

      // Keywords match
      if (command.keywords) {
        const keywordMatch = command.keywords.some((keyword) =>
          keyword.toLowerCase().includes(lowerQuery)
        );
        if (keywordMatch) {
          score += 150;
        }
      }

      // Category match
      if (command.category?.toLowerCase().includes(lowerQuery)) {
        score += 50;
      }

      // Only add priority as a tiebreaker if there's already a match
      if (score > 0 && command.priority) {
        score += command.priority;
      }

      return { command, score };
    });

    // Filter out commands with no match and sort by score
    return scored
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.command);
  }

  /**
   * Sort commands by priority and label
   * @param commands - Commands to sort
   * @returns Sorted commands
   */
  private sortCommands(commands: Command[]): Command[] {
    return commands.sort((a, b) => {
      // Sort by priority first (higher priority first)
      const priorityDiff = (b.priority || 0) - (a.priority || 0);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }

      // Then by category
      const categoryA = a.category || '';
      const categoryB = b.category || '';
      const categoryDiff = categoryA.localeCompare(categoryB);
      if (categoryDiff !== 0) {
        return categoryDiff;
      }

      // Finally by label
      return a.label.localeCompare(b.label);
    });
  }

  /**
   * Subscribe to registry changes
   * @param listener - Callback function called when registry changes
   * @returns Unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Clear all commands
   */
  clear(): void {
    this.commands.clear();
    this.notifyListeners();
  }

  /**
   * Get count of registered commands
   */
  get size(): number {
    return this.commands.size;
  }

  /**
   * Notify all listeners of registry changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

// Singleton instance for global command registry
let globalRegistry: CommandRegistryService | null = null;

/**
 * Get the global command registry instance
 * @returns Global CommandRegistryService instance
 */
export function getGlobalCommandRegistry(): CommandRegistryService {
  if (!globalRegistry) {
    globalRegistry = new CommandRegistryService();
  }
  return globalRegistry;
}

/**
 * Reset the global command registry (useful for testing)
 */
export function resetGlobalCommandRegistry(): void {
  globalRegistry = null;
}
